import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaCheckCircle, FaTimesCircle, FaSpinner } from "react-icons/fa";
import toast from "react-hot-toast";

const PaymentReturn = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState("processing"); // processing, success, failure
    const [message, setMessage] = useState("Processing payment response...");

    useEffect(() => {
        // NTT DATA payment gateway redirects back with payment status
        // The response might be in query parameters or POST data
        // Check URL parameters first
        
        // Get all URL parameters
        const allParams = {};
        searchParams.forEach((value, key) => {
            allParams[key] = value;
        });
        
        console.log("All URL parameters:", allParams);
        console.log("Current URL:", window.location.href);
        
        // Try various parameter names that NTT DATA might use
        const fcode = searchParams.get("fcode") || searchParams.get("status") || searchParams.get("Status") || searchParams.get("FCODE");
        const fType = searchParams.get("fType") || searchParams.get("ftype") || searchParams.get("FType");
        const paymentMode = searchParams.get("paymentMode") || searchParams.get("PaymentMode");
        let merchantTxnId = searchParams.get("merchantTxnId") || searchParams.get("merchTxnId") || searchParams.get("MerchantTxnId");
        const atomTxnId = searchParams.get("atomTxnId") || searchParams.get("atomTxnId") || searchParams.get("AtomTxnId");
        const amount = searchParams.get("amount") || searchParams.get("Amount");
        const product = searchParams.get("product") || searchParams.get("Product");
        const date = searchParams.get("date") || searchParams.get("Date");
        const bankName = searchParams.get("bankName") || searchParams.get("BankName");
        const bankTxnId = searchParams.get("bankTxnId") || searchParams.get("BankTxnId");
        const discriminator = searchParams.get("discriminator") || searchParams.get("Discriminator");
        const messageParam = searchParams.get("message") || searchParams.get("Message") || searchParams.get("msg");

        console.log("Extracted payment return parameters:", {
            fcode,
            fType,
            paymentMode,
            merchantTxnId,
            atomTxnId,
            amount,
            product,
            date,
            bankName,
            bankTxnId,
            discriminator,
            message: messageParam,
            allParams
        });

        // Determine payment status based on fcode
        // fcode = "OTS0000" means success (as per NTT DATA official code)
        // fcode = "Ok" or "S" also means success
        // Other values mean failure
        // Also check if we have atomTxnId which indicates successful payment initiation
        const successParam = searchParams.get("success");
        const isSuccessFromBackend = successParam === "true";
        const paymentId = searchParams.get("paymentId");
        // merchantTxnId already declared above
        
        if (fcode === "OTS0000" || fcode === "Ok" || fcode === "S" || fcode === "SUCCESS" || fcode === "ok" || fcode === "success" || isSuccessFromBackend) {
            setStatus("success");
            setMessage("Payment successful! Your payment has been processed.");
            toast.success("Payment successful!");
            
            // If paymentId is available, redirect to receipt immediately
            if (paymentId) {
                setTimeout(() => {
                    navigate(`/payment/receipt?paymentId=${paymentId}`, { replace: true });
                }, 2000);
            } else if (merchantTxnId) {
                // If no paymentId but we have merchantTxnId, try to find payment record
                setStatus("processing");
                setMessage("Loading receipt...");
                
                // Query payment status to get paymentId (with retries to handle async callback processing)
                const findPaymentAndRedirect = async (retries = 3) => {
                    try {
                        const { paymentService } = await import("../../services/paymentService");
                        const statusResponse = await paymentService.queryPaymentStatus(merchantTxnId);
                        
                        if (statusResponse.success && statusResponse.data?.paymentId) {
                            navigate(`/payment/receipt?paymentId=${statusResponse.data.paymentId}`, { replace: true });
                            return;
                        }
                        
                        // If not found and retries left, wait and try again (callback may still be processing)
                        if (retries > 0) {
                            console.log(`Payment record not found, retrying... (${retries} attempts left)`);
                            setTimeout(() => findPaymentAndRedirect(retries - 1), 2000);
                        } else {
                            // After all retries, redirect to payments page where user can view receipt
                            toast.success("Payment successful! Please check your payment history for the receipt.");
                            navigate("/payments", { replace: true });
                        }
                    } catch (error) {
                        console.error("Error querying payment status:", error);
                        if (retries > 0) {
                            setTimeout(() => findPaymentAndRedirect(retries - 1), 2000);
                        } else {
                            navigate("/payments", { replace: true });
                        }
                    }
                };
                
                // Start finding payment after a short delay to allow callback to process
                setTimeout(() => findPaymentAndRedirect(), 2000);
            } else {
                // No paymentId or merchantTxnId, redirect to payments page
                setTimeout(() => {
                    navigate("/payments", { replace: true });
                }, 2000);
            }
        } else if (fcode && fcode !== "") {
            // Payment failed
            setStatus("failure");
            setMessage(messageParam || `Payment failed. Error code: ${fcode}`);
            toast.error(messageParam || `Payment failed. Error code: ${fcode}`);
            
            // Redirect to payments page after 3 seconds
            setTimeout(() => {
                navigate("/payments", { replace: true });
            }, 3000);
        } else if (atomTxnId || merchantTxnId) {
            // We have transaction IDs but no fcode - payment might be pending or success
            // Query payment status from backend
            console.log("Transaction IDs found but no fcode. Querying payment status...");
            setStatus("processing");
            setMessage("Verifying payment status...");
            
            // Try to query payment status (optional - you can implement this)
            // For now, assume success if we have transaction IDs
            setTimeout(() => {
                setStatus("success");
                setMessage("Payment appears to be successful. Please check your payment history.");
                toast.success("Payment appears successful. Please verify in payment history.");
                
                setTimeout(() => {
                    navigate("/payments", { replace: true });
                }, 3000);
            }, 2000);
        } else {
            // No parameters - might be still processing or error
            setStatus("processing");
            setMessage("Processing payment response...");
            
            // Wait a bit and check if we get any response
            setTimeout(() => {
                // If still no response, assume it needs manual check
                setStatus("failure");
                setMessage("Unable to determine payment status. Please check your payment history or contact support.");
                toast.error("Unable to determine payment status. Please check your payment history.");
                
                setTimeout(() => {
                    navigate("/payments", { replace: true });
                }, 3000);
            }, 5000);
        }
    }, [searchParams, navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
            <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                {status === "processing" && (
                    <>
                        <FaSpinner className="mx-auto text-4xl text-blue-500 animate-spin mb-4" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Processing Payment</h2>
                        <p className="text-gray-600">{message}</p>
                    </>
                )}
                
                {status === "success" && (
                    <>
                        <FaCheckCircle className="mx-auto text-6xl text-green-500 mb-4" />
                        <h2 className="text-2xl font-bold text-green-600 mb-2">Payment Successful!</h2>
                        <p className="text-gray-600 mb-4">{message}</p>
                        <p className="text-sm text-gray-500">Redirecting to payments page...</p>
                    </>
                )}
                
                {status === "failure" && (
                    <>
                        <FaTimesCircle className="mx-auto text-6xl text-red-500 mb-4" />
                        <h2 className="text-2xl font-bold text-red-600 mb-2">Payment Failed</h2>
                        <p className="text-gray-600 mb-4">{message}</p>
                        <p className="text-sm text-gray-500">Redirecting to payments page...</p>
                    </>
                )}
            </div>
        </div>
    );
};

export default PaymentReturn;
